import { platformApi } from "@/api/http";

export interface VisitRecord {
  appointmentId: number;
  treatmentGoal?: string;
  productsUsed?: string;
  notes?: string;
  hasAllergyFlag: boolean;
  allergyFlagDetail?: string;
  hasBeforePhoto: boolean;
  hasAfterPhoto: boolean;
}

export interface SaveVisitRecordRequest {
  treatmentGoal?: string;
  productsUsed?: string;
  notes?: string;
  hasAllergyFlag?: boolean;
  allergyFlagDetail?: string;
}

export interface VisitTimelineEntry {
  appointmentId: number;
  appointmentDate: string;
  startTime: string;
  serviceName?: string;
  staffName?: string;
  treatmentGoal?: string;
  productsUsed?: string;
  notes?: string;
  hasAllergyFlag: boolean;
  allergyFlagDetail?: string;
  hasBeforePhoto: boolean;
  hasAfterPhoto: boolean;
}

export type PhotoSlot = "before" | "after";

function normalizeVisitRecord(raw: unknown): VisitRecord {
  const r = raw as Record<string, unknown>;
  return {
    appointmentId: Number(r.appointmentId ?? 0),
    treatmentGoal: (r.treatmentGoal as string) ?? undefined,
    productsUsed: (r.productsUsed as string) ?? undefined,
    notes: (r.notes as string) ?? undefined,
    hasAllergyFlag: Boolean(r.hasAllergyFlag),
    allergyFlagDetail: (r.allergyFlagDetail as string) ?? undefined,
    hasBeforePhoto: Boolean(r.hasBeforePhoto),
    hasAfterPhoto: Boolean(r.hasAfterPhoto),
  };
}

function normalizeTimelineEntry(raw: unknown): VisitTimelineEntry {
  const r = raw as Record<string, unknown>;
  return {
    appointmentId: Number(r.appointmentId ?? 0),
    appointmentDate: String(r.appointmentDate ?? ""),
    startTime: String(r.startTime ?? ""),
    serviceName: (r.serviceName as string) || undefined,
    staffName: (r.staffName as string) || undefined,
    treatmentGoal: (r.treatmentGoal as string) ?? undefined,
    productsUsed: (r.productsUsed as string) ?? undefined,
    notes: (r.notes as string) ?? undefined,
    hasAllergyFlag: Boolean(r.hasAllergyFlag),
    allergyFlagDetail: (r.allergyFlagDetail as string) ?? undefined,
    hasBeforePhoto: Boolean(r.hasBeforePhoto),
    hasAfterPhoto: Boolean(r.hasAfterPhoto),
  };
}

const appointmentVisitRecordPath = (businessId: string, appointmentId: number) =>
  `/api/v1/businesses/${businessId}/appointments/${appointmentId}/visit-record`;

export const visitRecordsApi = {
  save: async (
    businessId: string,
    appointmentId: number,
    request: SaveVisitRecordRequest,
  ): Promise<VisitRecord> => {
    const { data } = await platformApi.put(appointmentVisitRecordPath(businessId, appointmentId), request);
    return normalizeVisitRecord(data);
  },

  get: async (businessId: string, customerId: number, appointmentId: number): Promise<VisitRecord> => {
    const { data } = await platformApi.get(
      `/api/v1/businesses/${businessId}/customers/${customerId}/appointments/${appointmentId}/visit-record`,
    );
    return normalizeVisitRecord(data);
  },

  getTimeline: async (businessId: string, customerId: number): Promise<VisitTimelineEntry[]> => {
    const { data } = await platformApi.get(
      `/api/v1/businesses/${businessId}/customers/${customerId}/visit-timeline`,
    );
    return Array.isArray(data) ? data.map(normalizeTimelineEntry) : [];
  },

  uploadPhoto: async (
    businessId: string,
    appointmentId: number,
    slot: PhotoSlot,
    file: File,
  ): Promise<VisitRecord> => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await platformApi.post(
      `${appointmentVisitRecordPath(businessId, appointmentId)}/photos/${slot}`,
      formData,
    );
    return normalizeVisitRecord(data);
  },

  /**
   * Fetches photo bytes as an object URL. Photos are private blobs — never render this path
   * directly as an `<img src>`, since the endpoint requires the bearer token this call attaches.
   * Caller must revoke the returned URL (`URL.revokeObjectURL`) when done with it.
   */
  fetchPhotoObjectUrl: async (
    businessId: string,
    appointmentId: number,
    slot: PhotoSlot,
  ): Promise<string> => {
    const { data } = await platformApi.get(
      `${appointmentVisitRecordPath(businessId, appointmentId)}/photos/${slot}/preview`,
      { responseType: "blob" },
    );
    return URL.createObjectURL(data as Blob);
  },
};
